import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import store from '@/store/store';
import formatIsoDate from '@/services/general/formatIsoDate';
import { insertPieceJointe, updatePieceJointe } from '@/services/piecesJointes/savePiecesJointes';
import {
  getProduitFaceLc,
  createAllProduitFaceLcAsync,
  updateProduitFaceLcAsync,
  getReferencesProduitFace,
} from '@/api/produit';
import { majProduitFaceByPanneau, majProduitFaceByProduit, getProduitFaceBase, majFormatIdProduit } from '@/services/creationProduit/produitFace';
import calculArrondi from '@/services/calcul/calculArrondi';
import { getLogosByIds } from '@/services/general/indexDbLogoService';
import getSupportCommercial from '@/services/creationProduit/getSupportCommercial';
import { incrementRef, getProduitDefault } from '@/services/general/genererReferenceProduit';
import { CATEGORIE_UNITAIRE, PREFIXE_REFERENCE_FACE } from '@/configuration/produitConf';
import { genererDataLocation } from '@/services/recommandation/produitAssociersGeneration';
import { getAgenceId } from '@/services/recommandation/recommandationToDevis';
import { getLogoFileFromDataURI } from '@/services/concurrent/concurrent';
import generePJ from '@/services/piecesJointes/genererPiecesJointes';
import { MODULE_PRODUIT_UNITAIRE, PIECE_JOINTE_TYPE_LOGO } from '@/configuration/pieceJointeConf';

function preparationEnvoiBackProduit(produit) {
  const res = cloneDeep(produit);
  if (res.liaisonsTarif) {
    res.liaisonsTarif.forEach(l => {
      l.quantite = calculArrondi(l.quantite, 2);
      l.dateDebut = formatIsoDate(l.dateDebut);
      l.dateFin = formatIsoDate(l.dateFin);
      l.liaisonsTarif.forEach(lt => {
        lt.quantite = calculArrondi(lt.quantite, 2);
        lt.dateDebut = formatIsoDate(lt.dateDebut);
        lt.dateFin = formatIsoDate(lt.dateFin);
      });
    });
  }
  if (!res.supportCommercialId && produit.supportCommercial) {
    res.supportCommercialId = produit.supportCommercial.id;
  }
  return res;
}

const DATA_LOCATION = [
  { propriete: 'location', hasRegie: true, produitField: null },
  { propriete: 'tlpe', hasRegie: true, produitField: 'produitTlpe' },
  { propriete: 'honoraires', hasRegie: false, produitField: 'produitHonoraire' },
  { propriete: 'fraisTechnique', hasRegie: false, produitField: 'produitFraisTechnique' },
  { propriete: 'honoraireFraisTraitement', hasRegie: false, produitField: 'produitHonoraireFraisTraitement' },
];

export function genererLigneBudget(panneau, budget) {
  const societe = store.getters['societe/societeActive'];
  const result = DATA_LOCATION.reduce((acc, val) => {
    const produit = val.propriete === 'location'
      ? getProduitDefault(panneau.categorie)
      : societe[val.produitField];
    acc[val.propriete] = genererDataLocation({
      produit,
      propriete: val.propriete,
      fournisseurId: val.hasRegie ? panneau.regieId : null,
      ligneCommandeId: get(budget.ligneCommandeIds, val.propriete, null),
    }, budget);
    return acc;
  }, {});
  return result;
}

function getLiaisonTarifProduit(panneau) {
  const { location, tlpe, honoraires, fraisTechnique} = genererLigneBudget(panneau, panneau.budgets[0]);
  return [{ ...location, liaisonsTarif: [tlpe, honoraires, fraisTechnique].filter(Boolean) }];
}

function genererProduit(supportCommerical, references, panneau) {
  const agenceId = getAgenceId();
  const produit = getProduitFaceBase();
  majProduitFaceByPanneau(produit, panneau, supportCommerical, agenceId);
  if (references.length) {
    const reference = incrementRef(references[PREFIXE_REFERENCE_FACE]);
    produit.reference = reference;
    references[PREFIXE_REFERENCE_FACE] = reference;
    produit.produitFace.codePanneau = reference;
    if (!produit.libelle) produit.libelle = reference;
  }
  produit.liaisonsTarif = getLiaisonTarifProduit(panneau);
  produit.produitFace.produitParDefautId = location.produitEnfantId;
  return produit;
}

async function gestionPiecesJointe(produits) {
  const images = await getLogosByIds(produits.map(p => p.guid));
  const filesData = produits.map(p => {
    const file = getLogoFileFromDataURI(images[p.guid] || p.srcImg);
    if (!file) return null;
    return { file, placeType: PIECE_JOINTE_TYPE_LOGO, guid: p.guid };
  }).filter(Boolean);
  const pieceJointesData = await generePJ(filesData);
  const promises = filesData.map((fileData, index) => {
    const produit = produits.find(p => p.guid === fileData.guid);
    const funcAsync = produit.urlLogo ? updatePieceJointe : insertPieceJointe;
    const pj = pieceJointesData[index];
    return funcAsync([pj], produit.id, MODULE_PRODUIT_UNITAIRE);
  });
  await Promise.all(promises);
}

async function panneauToProduitCreateAsync(panneau) {
  const societe = store.getters['societe/societeActive'];
  const supportCommerical = await getSupportCommercial(CATEGORIE_UNITAIRE, societe.id);
  const res = genererProduit(supportCommerical, [], panneau);
  res.reference = PREFIXE_REFERENCE_FACE;
  return preparationEnvoiBackProduit(res);
}

async function panneauToProduitUpdateAsync(panneau) {
  const produit = (await getProduitFaceLc(panneau.id)).data.result;
  majProduitFaceByPanneau(produit, panneau, null, getAgenceId());
  return produit;
}

export async function panneauToProduitAsync(panneau) {
  const funcAsync = panneau.id ? panneauToProduitUpdateAsync : panneauToProduitCreateAsync;
  const produit = await funcAsync(panneau);
  return produit;
}

export async function createProduits(produitsToCreate) {
  if (!produitsToCreate.length) return [];
  const references = await getReferencesProduitFace(produitsToCreate.map(p => p.reference));
  const produits = produitsToCreate.map(p => {
    const ref = p.reference;
    const reference = incrementRef(references[ref]);
    p.reference = reference;
    p.produitFace.codePanneau = reference;
    if (!p.libelle) p.libelle = reference;
    references[ref] = reference;
    return preparationEnvoiBackProduit(p);
  });
  const response = (await createAllProduitFaceLcAsync(produits)).data.result;
  return response.map((r, index) => ({ ...r, guid: produits[index].guid }));
}

export async function updateProduits(produitToUpdate) {
  if (!produitToUpdate.length) return [];
  let promises = produitToUpdate.map(p => getProduitFaceLc(p.id));
  const produits = (await Promise.all(promises)).map(p => p.data.result);
  promises = produitToUpdate.map(async (p) => {
    let produit = majProduitFaceByProduit(produits.find(el => el.id === p.id), p);
    majFormatIdProduit(produit);
    produit = preparationEnvoiBackProduit(produit);
    const resultProduit = (await updateProduitFaceLcAsync(produit)).data.result;
    resultProduit.guid = p.guid;
    resultProduit.urlLogo = p.urlLogo;
    return resultProduit;
  });
  return await Promise.all(promises);
}

export default async function createOrUpdateProduits(produitsDevis) {
  const promises = [
    createProduits(produitsDevis.filter(p => !p.id)),
    updateProduits(produitsDevis.filter(p => p.id)),
  ];
  const produits = (await Promise.all(promises)).flat();
  await gestionPiecesJointe(produits);
  return produits;
}
